/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { applicationModelNames } from 'client/util/Constants/ApplicationConstants';
import ApplicationTemplateApiV2 from 'client/api/v2/ApplicationTemplateApiV2';
import ApplicationTemplatesFilters from 'client/components/ItemTable/CollectionFilterComponents/constants/ApplicationTemplatesFilters';
import CollectionModule from 'client/store/modules/CollectionModule';
import Vue from 'vue';

const { SAML2, BOOKMARK, OIDC } = applicationModelNames;

export class ApplicationTemplateCollectionClass extends CollectionModule {
  constructor(Api = ApplicationTemplateApiV2, idField = '_id', sortBy = 'name') {
    super(Api, idField, sortBy);
  }

  getInitialState() {
    const state = super.getInitialState();
    state.features.items.filters = [
      ApplicationTemplatesFilters.appStatusEos(),
      ApplicationTemplatesFilters.appStatusEol(),
    ];
    return {
      ...state,
      featuredApplications: [],
      genericBookmarkId: '',
      genericTemplateId: '',
      genericOidcTemplateId: '',
      isLoading: false,
    };
  }

  extractModule() {
    const module = super.extractModule();

    module.actions.fetchFeaturedTemplates = (...params) => this.fetchFeaturedTemplates(...params);
    module.actions.fetchGenericTemplate = (...params) => this.fetchGenericTemplate(...params);
    module.actions.fetchGenericBookmark = (...params) => this.fetchGenericBookmark(...params);
    module.actions.fetchGenericOidcTemplate = this.fetchGenericOidcTemplate.bind(this);

    module.getters.genericBookmarkId = (...params) => this.genericBookmarkId(...params);
    module.getters.genericOidcTemplateId = this.genericOidcTemplateId.bind(this);
    module.getters.genericTemplateId = (...params) => this.genericTemplateId(...params);
    module.getters.getFeaturedApplications = (...params) => this.getFeaturedApplications(...params);
    module.getters.getIsLoading = (...params) => this.getIsLoading(...params);

    module.mutations.SET_BOOKMARK_TEMPLATE_ID = (...params) => this
      .SET_BOOKMARK_TEMPLATE_ID(...params);

    module.mutations.SET_FEATURED_APPLICATIONS = (...params) => this
      .SET_FEATURED_APPLICATIONS(...params);

    module.mutations.SET_GENERIC_OIDC_TEMPLATE_ID = this.SET_GENERIC_OIDC_TEMPLATE_ID.bind(this);

    module.mutations.SET_GENERIC_TEMPLATE_ID = (...params) => this
      .SET_GENERIC_TEMPLATE_ID(...params);

    module.mutations.SET_IS_LOADING = (...params) => this.SET_IS_LOADING(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  async fetchFeaturedTemplates({ commit }, { appList = [] }) {
    const collection = [];
    const tasks = [];

    commit('SET_IS_LOADING', true);

    appList.forEach((app) => {
      tasks.push(ApplicationTemplateApiV2.fetch(0, 5, 'name', app));
    });

    // Fetch all of the templates asynchronously
    try {
      const responses = await Promise.all(tasks);

      responses.forEach((response, index) => {
        // There can be multiple templates with a similar name - find the exact match
        const item = response.results.find(({ name }) => name === appList[index]);
        if (item) {
          collection.push(item);
        }
      });
    } catch {
      // Don't do anything, just save an empty collection
    }

    commit('SET_FEATURED_APPLICATIONS', collection);

    commit('SET_IS_LOADING', false);
  }

  async fetchGenericTemplate({ state, commit }) {
    if (state.genericTemplateId === '') {
      let item;
      const { results } = await this.Api.fetch(0, 5, 'name', SAML2);
      if (results.length) {
        item = results.find(({ name }) => name === SAML2);
      }

      if (item) {
        commit('SET_GENERIC_TEMPLATE_ID', item[this.idField]);
        commit('SET_ITEM', item);
      }
    }
  }

  async fetchGenericBookmark({ state, commit }) {
    if (state.genericBookmarkId === '') {
      let item;
      const { results } = await this.Api.fetch(0, 5, 'name', BOOKMARK);

      if (results.length) {
        item = results.find(({ name }) => name === BOOKMARK);
      }

      if (item) {
        commit('SET_BOOKMARK_TEMPLATE_ID', item[this.idField]);
        commit('SET_ITEM', item);
      }
    }
  }

  async fetchGenericOidcTemplate({ commit, state }) {
    if (!state.genericOidcTemplateId) {
      const { results } = await this.Api.fetch(0, 1, 'name', OIDC);

      if (results.length) {
        const [item] = results;

        commit('SET_GENERIC_OIDC_TEMPLATE_ID', item[this.idField]);
        commit('SET_ITEM', item);
      }
    }
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  genericBookmarkId({ genericBookmarkId }) {
    return genericBookmarkId;
  }

  genericOidcTemplateId({ genericOidcTemplateId }) {
    return genericOidcTemplateId;
  }

  genericTemplateId({ genericTemplateId }) {
    return genericTemplateId;
  }

  getFeaturedApplications(state) {
    return state.featuredApplications;
  }

  getIsLoading(state) {
    return state.isLoading;
  }

  getCollection(state = { features: { items: { ids: [] } } }) {
    return state.features.items.ids.reduce((accumulator, id) => {
      if (state.entities.items.byId[id].displayName !== 'Zoom') {
        accumulator.push(state.entities.items.byId[id]);
      }
      return accumulator;
    }, []);
  }

  getTotalCount(state = { entities: { items: { totalCount: 0 } } }) {
    if (state.entities.items.totalCount > 0) {
      return state.entities.items.totalCount - 1;
    }
    return state.entities.items.totalCount;
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  SET_BOOKMARK_TEMPLATE_ID(state, id = '') {
    Vue.set(state, 'genericBookmarkId', id);
  }

  SET_FEATURED_APPLICATIONS(state, items = []) {
    Vue.set(state, 'featuredApplications', items);
  }

  SET_FILTER(state, { filter }) {
    const { filters } = state.features.items;
    filters.forEach((fil, index) => {
      if (fil.id === filter.id) {
        Vue.set(state.features.items.filters, index, filter);
      }
    });
  }

  SET_GENERIC_OIDC_TEMPLATE_ID(state, id = '') {
    Vue.set(state, 'genericOidcTemplateId', id);
  }

  SET_GENERIC_TEMPLATE_ID(state, id = '') {
    Vue.set(state, 'genericTemplateId', id);
  }

  SET_IS_LOADING(state, isLoading) {
    Vue.set(state, 'isLoading', isLoading);
  }
}

export default new ApplicationTemplateCollectionClass().extractModule();
